/** Time tracking + leave data hooks — spec §5-F9, §5-F23, §7.7c. */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Designation, LeaveStatus, LeaveType } from '@virtuetasks/types';
import { api, qs } from '@/lib/api';

/* ── Time ──────────────────────────────────────────────── */

export interface TimesheetDay {
  date: string;
  minutes: number;
  isWorkingDay: boolean;
}

export interface Timesheet {
  userId: string;
  from: string;
  to: string;
  totalMinutes: number;
  days: TimesheetDay[];
  byProject: { projectId: string; projectKey: string; projectName: string; minutes: number }[];
  submittedMinutes: number;
  approvedMinutes: number;
}

export interface TimeEntryView {
  _id: string;
  taskId: string;
  taskKey?: string;
  taskTitle?: string;
  projectId: string;
  projectKey?: string;
  userId: string;
  startedAt: string;
  endedAt?: string | null;
  durationMinutes: number;
  description?: string;
  billable: boolean;
  approvalStatus: 'pending' | 'submitted' | 'approved' | 'rejected';
}

/**
 * `from`/`to` are date-only (`YYYY-MM-DD`), not Dates.
 *
 * The server bins days in UTC. Sending a local midnight from IST (UTC+5:30)
 * lands on the previous UTC day, so a Mon–Sun week came back as eight columns.
 * Date-only strings are unambiguous: both sides agree they mean UTC midnight.
 */
export function useTimesheet(from: string, to: string, userId?: string) {
  return useQuery({
    queryKey: ['timesheet', from, to, userId],
    queryFn: async () =>
      (await api.get<Timesheet>(`/time/timesheet${qs({ from, to, userId })}`)).data,
  });
}

export function useTimeEntries(from: string, to: string, userId?: string) {
  return useQuery({
    queryKey: ['time-entries', from, to, userId],
    queryFn: async () =>
      (await api.get<TimeEntryView[]>(`/time/entries${qs({ from, to, userId, limit: 200 })}`)).data,
  });
}

export function useRunningTimer() {
  return useQuery({
    queryKey: ['running-timer'],
    queryFn: async () => (await api.get<TimeEntryView | null>('/time/running')).data,
    refetchInterval: 60_000,
  });
}

export function useStartTimer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (taskId: string) =>
      (await api.post<TimeEntryView>('/time/start', { taskId })).data,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['running-timer'] });
      void qc.invalidateQueries({ queryKey: ['timesheet'] });
    },
  });
}

export function useStopTimer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => (await api.post<TimeEntryView>('/time/stop')).data,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['running-timer'] });
      void qc.invalidateQueries({ queryKey: ['timesheet'] });
      void qc.invalidateQueries({ queryKey: ['time-entries'] });
    },
  });
}

export interface LogTimeInput {
  taskId: string;
  startedAt: string;
  durationMinutes: number;
  description?: string;
  billable?: boolean;
}

export function useLogTime() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: LogTimeInput) =>
      (await api.post<TimeEntryView>('/time/entries', input)).data,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['timesheet'] });
      void qc.invalidateQueries({ queryKey: ['time-entries'] });
    },
  });
}

export function useDeleteTimeEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => api.delete(`/time/entries/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['timesheet'] });
      void qc.invalidateQueries({ queryKey: ['time-entries'] });
    },
  });
}

export function useSubmitTimesheet() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (range: { from: string; to: string }) =>
      (await api.post<{ submitted: number }>('/time/timesheet/submit', range)).data,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['timesheet'] });
      void qc.invalidateQueries({ queryKey: ['time-entries'] });
    },
  });
}

/* ── Leave ─────────────────────────────────────────────── */

export interface LeaveView {
  _id: string;
  userId: string;
  /** V4 B-1: the requester travels with the row — see `withRequesters()`. */
  userName: string;
  userDesignation?: Designation | null;
  userAvatarUrl?: string | null;
  /** Working days the request costs, per the workspace calendar. */
  workingDays: number;
  type: LeaveType;
  startDate: string;
  endDate: string;
  status: LeaveStatus;
  reason?: string;
  decidedBy?: string;
  /** V10 §6 — who decided it, by name. Null while pending. */
  deciderName?: string | null;
  approvedBy?: string;
  decidedAt?: string;
  decisionNote?: string;
  createdAt: string;
}

export interface LeaveFilters {
  userId?: string;
  status?: LeaveStatus;
  /** V10 §6 — several statuses at once, for the decided-requests history. */
  statuses?: LeaveStatus[];
  /** ISO dates. `from` matches leaves ending on or after it. */
  from?: string;
  to?: string;
  sort?: 'recent' | 'soonest';
  limit?: number;
}

export function useLeaves(filters: LeaveFilters = {}, enabled = true) {
  return useQuery({
    queryKey: ['leaves', filters],
    enabled,
    queryFn: async () =>
      (
        await api.get<LeaveView[]>(
          `/leaves${qs({
            ...filters,
            statuses: filters.statuses?.join(','),
            limit: filters.limit ?? 100,
          })}`,
        )
      ).data,
  });
}

export interface RequestLeaveInput {
  type: LeaveType;
  startDate: string;
  endDate: string;
  reason?: string;
}

export function useRequestLeave() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: RequestLeaveInput) =>
      (await api.post<LeaveView>('/leaves', input)).data,
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['leaves'] }),
  });
}

export function useLeaveDecision() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      action,
      note,
    }: {
      id: string;
      action: 'approve' | 'reject' | 'cancel' | 'revoke';
      note?: string;
    }) => (await api.post<LeaveView>(`/leaves/${id}/${action}`, note ? { note } : {})).data,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['leaves'] });
      void qc.invalidateQueries({ queryKey: ['workload'] });
    },
  });
}
